import {useEffect, useRef} from 'react';

/**
 * A custom React hook that returns a ref indicating whether the component is unmounted.
 *
 * @returns {Object} - A ref object with a boolean value indicating if the component is unmounted.
 */
const useIsUnmounted = () => {
  const isUnmountedRef = useRef(false);
  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  return isUnmountedRef;
};

/**
 * A custom React hook that creates a debounced function from the provided function.
 * The debounced function will be called only if the component is not unmounted.
 *
 * @param {Function} fun - The raw function to be debounced.
 * @param {number} ms - The time in milliseconds for the debounce.
 * @returns {Function} - The debounced function.
 */
export const useDebounced = (fun, ms) => {
  const isUnmountedRef = useIsUnmounted();
  const timeoutRef = useRef();

  const debouncedFun = (...args) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (!isUnmountedRef.current) {
        fun(...args);
      }
    }, ms);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return debouncedFun;
};
